import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { fileToCompressedBase64 } from "@/lib/image-compress";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ToastAction } from "@/components/ui/toast";
import { useToast } from "@/hooks/use-toast";
import {
  Camera, Upload, X, Loader2, Copy, Save, Trash2, ClipboardList, FilePlus2, Sparkles, ArrowLeft,
  FileText, Image as ImageIcon, Info, ArrowRight,
} from "lucide-react";
import type { PhotoAnalysis, PhotoAnalysisReportType, Project, SuggestedPosition, LibraryPosition } from "@shared/schema";
import { pickPriceForQuantity, libraryLookupKey } from "@shared/library-pricing";
import { STAMMDATEN } from "@/components/project/leistungsverzeichnis-kalkulator";
import type { PhotoLVPosition } from "@/lib/storage";
import type { ServerPhotoMetadata } from "@/lib/photo-api";
import { useIsMobile } from "@/hooks/use-mobile";
import { DesktopOnlyNotice } from "./mobile/desktop-only-notice";

interface CustomPositionTemplate {
  projectId: number;
  projectName: string;
  sektionId: string;
  name: string;
  unit: string;
  ep: number;
  qty: number;
  tag: string | null;
}

interface SuggestedDraft {
  id: string;
  checked: boolean;
  kurztext: string;
  einheit: string;
  menge: number;
  ep: number;
  einzelpreisVon: number;
  einzelpreisBis: number;
  begruendung: string;
  ownMedian: number | null;
  ownCount: number;
  ownSource: "library" | "projekt" | null;
}

function normalizeKey(name: string, unit: string): string {
  return `${name.trim().toLowerCase().replace(/\s+/g, " ")}::${unit.trim().toLowerCase()}`;
}

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

function fmtEur(n: number): string {
  return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(n);
}

const REPORT_TYPE_LABEL: Record<PhotoAnalysisReportType, string> = {
  schadensbericht: "Schadensbericht",
  leistungsbeschreibung: "Leistungsbeschreibung",
};

const ACCEPTED_MIME = /^image\/(png|jpeg|jpg|webp|gif)$/i;
const MAX_IMAGES = 8;
const MAX_TOTAL_BYTES = 18 * 1024 * 1024;

interface SelectedImage {
  id: string;
  file: File;
  previewUrl: string;
}

interface AnalyzePayload {
  reportType: PhotoAnalysisReportType;
  context: string;
  images: Array<{ fileName: string; mimeType: string; data: string }>;
}

function readPresetProjectId(): string {
  if (typeof window === "undefined") return "";
  const params = new URLSearchParams(window.location.search);
  const raw = params.get("projectId") ?? "";
  return /^\d+$/.test(raw) ? raw : "";
}

function readPresetPhotoId(): string {
  if (typeof window === "undefined") return "";
  const params = new URLSearchParams(window.location.search);
  const raw = params.get("photoId") ?? "";
  return /^\d+$/.test(raw) ? raw : "";
}

export default function PhotoAnalysisPage() {
  const isMobile = useIsMobile();
  if (isMobile) {
    return (
      <DesktopOnlyNotice
        title="Foto-Analyse"
        description="Die KI-gestützte Foto-Analyse arbeitet am Computer mit großen Bildern und langen Texten. Bitte am Desktop öffnen."
      />
    );
  }
  return <DesktopPhotoAnalysisPage />;
}

function DesktopPhotoAnalysisPage() {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [presetProjectId] = useState<string>(() => readPresetProjectId());
  const [presetPhotoId] = useState<string>(() => readPresetPhotoId());
  const [presetPhotoLoading, setPresetPhotoLoading] = useState<boolean>(() => !!readPresetProjectId() && !!readPresetPhotoId());
  const [presetPhotoError, setPresetPhotoError] = useState<string | null>(null);
  const [lastAdoptedProject, setLastAdoptedProject] = useState<{ id: number; name: string; photoId?: number } | null>(null);
  const [reportType, setReportType] = useState<PhotoAnalysisReportType>("schadensbericht");
  const [context, setContext] = useState("");
  const [images, setImages] = useState<SelectedImage[]>([]);
  const [activeAnalysis, setActiveAnalysis] = useState<PhotoAnalysis | null>(null);
  const [editedText, setEditedText] = useState("");
  const [adoptOpen, setAdoptOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const dropRef = useRef<HTMLDivElement | null>(null);
  const [dragActive, setDragActive] = useState(false);

  // Adopt dialog state
  const [adoptProjectId, setAdoptProjectId] = useState<string>(presetProjectId);
  const [adoptMode, setAdoptMode] = useState<"new" | "existing">("new");
  const [adoptExistingId, setAdoptExistingId] = useState<string>("");
  const [adoptExistingMerge, setAdoptExistingMerge] = useState<"replace" | "append">("replace");
  const [adoptSektionId, setAdoptSektionId] = useState<string>("s1");
  const [adoptName, setAdoptName] = useState<string>("");
  const [adoptUnit, setAdoptUnit] = useState<string>("Psch");
  const [adoptEp, setAdoptEp] = useState<number>(0);
  const [adoptQty, setAdoptQty] = useState<number>(1);

  // Suggested-positions (multi-adopt) state
  const [suggestedDrafts, setSuggestedDrafts] = useState<SuggestedDraft[]>([]);
  /** Markiert eine Analyse-ID, für die nach erfolgreichem Draft-Aufbau der
   *  Multi-Übernahme-Dialog automatisch geöffnet werden soll. Wird vom
   *  Schnellweg `quickAdoptFromHistory` gesetzt und vom useEffect, der die
   *  Drafts aus `activeAnalysis` baut, konsumiert — verhindert die
   *  Race-Condition, dass der Dialog mit veralteten Drafts öffnet. */
  const [pendingAdoptForAnalysisId, setPendingAdoptForAnalysisId] = useState<number | null>(null);
  const [adoptMultiOpen, setAdoptMultiOpen] = useState(false);
  const [adoptMultiProjectId, setAdoptMultiProjectId] = useState<string>(presetProjectId);
  // Ziel: Schadensbild (Foto), an das die Vorschläge angehängt werden.
  // Wenn die Seite mit ?photoId=… aufgerufen wurde, ist das Ziel-Foto bereits
  // bestimmt — der Auswahl-Schritt im Übernahme-Dialog wird in dem Fall
  // gesperrt, damit keine falsche Zuordnung möglich ist.
  const [adoptMultiPhotoId, setAdoptMultiPhotoId] = useState<string>(presetPhotoId);

  // Cleanup object URLs only on unmount (per-image revocation happens in removeImage)
  const imagesRef = useRef<SelectedImage[]>([]);
  useEffect(() => { imagesRef.current = images; }, [images]);
  useEffect(() => () => { imagesRef.current.forEach((i) => URL.revokeObjectURL(i.previewUrl)); }, []);

  // Pre-Load: wenn ?projectId=…&photoId=… in der URL stehen, das Foto direkt
  // vom Server holen und als einziges Eingabebild eintragen. Damit muss der
  // Nutzer nichts erneut hochladen, wenn er aus dem Projekt-Editor in die
  // Foto-Analyse gesprungen ist. Läuft genau einmal pro Pageload.
  const presetLoadConsumedRef = useRef(false);
  useEffect(() => {
    if (presetLoadConsumedRef.current) return;
    if (!presetProjectId || !presetPhotoId) return;
    presetLoadConsumedRef.current = true;
    let cancelled = false;
    const photoIdNum = Number(presetPhotoId);
    (async () => {
      try {
        const metaRes = await fetch(`/api/projects/${presetProjectId}/photos`, {
          credentials: "include",
        });
        if (!metaRes.ok) throw new Error("Foto-Metadaten konnten nicht geladen werden.");
        const metaList = (await metaRes.json()) as ServerPhotoMetadata[];
        const meta = metaList.find((p) => p.id === photoIdNum);
        if (!meta) throw new Error("Foto nicht im Projekt gefunden.");

        const binRes = await fetch(`/api/photos/${photoIdNum}`, { credentials: "include" });
        if (!binRes.ok) throw new Error("Foto-Datei konnte nicht geladen werden.");
        const blob = await binRes.blob();
        const mime = meta.mimeType || blob.type || "image/jpeg";
        const file = new File(
          [blob],
          meta.fileName || `foto-${photoIdNum}.jpg`,
          { type: mime },
        );
        if (cancelled) return;
        const newImage: SelectedImage = {
          id: `preset-photo-${photoIdNum}`,
          file,
          previewUrl: URL.createObjectURL(file),
        };
        setImages([newImage]);
        setPresetPhotoLoading(false);
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : "Unbekannter Fehler";
        setPresetPhotoError(msg);
        setPresetPhotoLoading(false);
        toast({
          title: "Foto konnte nicht vorgeladen werden",
          description: msg,
          variant: "destructive",
        });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetProjectId, presetPhotoId]);

  const { data: history = [], isLoading: historyLoading } = useQuery<PhotoAnalysis[]>({
    queryKey: ["/api/photo-analyses"],
  });

  const { data: projects = [] } = useQuery<Project[]>({
    queryKey: ["/api/projects"],
    enabled: adoptOpen || adoptMultiOpen,
  });

  // WICHTIG: Cache-Key MUSS exakt dem von project-edit.tsx entsprechen
  // (dort: ["/api/projects", projectId] mit projectId als String aus der URL).
  // Sonst landen Invalidierungen nach dem Adopt im falschen Cache-Slot und die
  // Projekt-Edit-Seite zeigt nach Rückkehr noch den alten lvState ohne die
  // frisch übernommenen customPositionen.
  const { data: selectedProject } = useQuery<Project & { lvState?: Record<string, unknown> | null }>({
    queryKey: ["/api/projects", adoptProjectId],
    enabled: (adoptOpen || adoptProjectId === presetProjectId) && !!adoptProjectId,
  });

  // Eigene Preis-Historie aus allen Projekten des Nutzers, um KI-Vorschläge
  // mit eigenen Erfahrungswerten abzugleichen.
  const { data: ownHistory = [] } = useQuery<CustomPositionTemplate[]>({
    queryKey: ["/api/templates/custom-positions"],
  });
  // Zusätzlich: zentrale Positions-Wissensdatenbank. Pro Eintrag gibt es
  // Preis-Samples (menge, ep, …), die mengen-abhängig konsultiert werden.
  const { data: libraryPositions = [] } = useQuery<LibraryPosition[]>({
    queryKey: ["/api/library-positions"],
  });

  // Klassischer Projekt-Median (ohne Mengen-Kontext) aus Custom-Position-Historie.
  const ownPriceIndex = useMemo(() => {
    const map = new Map<string, number[]>();
    for (const row of ownHistory) {
      if (!row.name || !row.unit || !(row.ep > 0)) continue;
      const key = normalizeKey(row.name, row.unit);
      const arr = map.get(key) ?? [];
      arr.push(row.ep);
      map.set(key, arr);
    }
    return map;
  }, [ownHistory]);

  // Library-Index: key → Eintrag mit Samples.
  const libraryIndex = useMemo(() => {
    const map = new Map<string, LibraryPosition>();
    for (const lp of libraryPositions) {
      map.set(libraryLookupKey(lp.name, lp.unit), lp);
    }
    return map;
  }, [libraryPositions]);

  function lookupOwnPrice(
    name: string,
    unit: string,
    menge: number,
  ): { median: number | null; count: number; source: "library" | "projekt" | null } {
    const lp = libraryIndex.get(libraryLookupKey(name, unit));
    const samples = lp?.priceSamples ?? [];
    if (samples.length > 0) {
      const pick = pickPriceForQuantity(samples, menge, lp?.ep ?? 0);
      if (pick.source !== "fallback") {
        return { median: pick.ep, count: samples.length, source: "library" };
      }
    }
    const arr = ownPriceIndex.get(normalizeKey(name, unit));
    if (!arr || arr.length === 0) return { median: null, count: 0, source: null };
    return { median: median(arr), count: arr.length, source: "projekt" };
  }

  // Drafts NUR dann komplett neu aufbauen, wenn sich die aktive Analyse
  // (ID) ändert. Preis-Indizes sind hier bewusst KEINE Dependency, sonst
  // überschreibt ein Hintergrund-Refresh der Bibliotheks-/Eigenpreisdaten
  // die manuell bearbeiteten Mengen/EPs beim Nutzer.
  // EP-Default: Eigener Median, sonst Mittelwert der KI-Spanne, sonst obere Grenze, sonst 0.
  useEffect(() => {
    const src = (activeAnalysis?.suggestedPositions ?? []) as SuggestedPosition[];
    if (!Array.isArray(src) || src.length === 0) {
      setSuggestedDrafts([]);
      // Falls auf eine Analyse OHNE Vorschläge gewartet wurde (Schnellweg
      // aus Verlauf), das Pending-Flag freiräumen — sonst würde es ewig
      // hängen, falls der Nutzer zwischendurch zu einer leeren Analyse wechselt.
      if (pendingAdoptForAnalysisId !== null) setPendingAdoptForAnalysisId(null);
      return;
    }
    const drafts: SuggestedDraft[] = src.map((s, idx) => {
      const menge = s.menge > 0 ? s.menge : 0;
      const own = lookupOwnPrice(s.kurztext, s.einheit, menge);
      const aiMid =
        s.einzelpreisVon > 0 && s.einzelpreisBis > 0
          ? (s.einzelpreisVon + s.einzelpreisBis) / 2
          : s.einzelpreisBis > 0
          ? s.einzelpreisBis
          : s.einzelpreisVon > 0
          ? s.einzelpreisVon
          : 0;
      const ep = own.median ?? aiMid;
      return {
        id: `draft-${activeAnalysis?.id ?? "x"}-${idx}`,
        checked: true,
        kurztext: s.kurztext,
        einheit: s.einheit,
        menge: s.menge > 0 ? s.menge : 1,
        ep: Math.round(ep * 100) / 100,
        einzelpreisVon: s.einzelpreisVon,
        einzelpreisBis: s.einzelpreisBis,
        begruendung: s.begruendung ?? "",
        ownMedian: own.median,
        ownCount: own.count,
        ownSource: own.source,
      };
    });
    setSuggestedDrafts(drafts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAnalysis?.id]);

  // Schnellweg-Trigger: sobald die Drafts für die per quickAdoptFromHistory
  // markierte Analyse aufgebaut sind, den Multi-Übernahme-Dialog öffnen.
  // Wir prüfen über die stable Draft-ID (`draft-{analysisId}-…`), dass die
  // Drafts wirklich zur erwarteten Analyse gehören — so kann sich kein
  // veralteter Snapshot in den Dialog mogeln.
  useEffect(() => {
    if (pendingAdoptForAnalysisId === null) return;
    if (activeAnalysis?.id !== pendingAdoptForAnalysisId) return;
    if (suggestedDrafts.length === 0) return;
    const expectedPrefix = `draft-${pendingAdoptForAnalysisId}-`;
    if (!suggestedDrafts[0].id.startsWith(expectedPrefix)) return;
    setAdoptMultiOpen(true);
    setPendingAdoptForAnalysisId(null);
  }, [pendingAdoptForAnalysisId, activeAnalysis?.id, suggestedDrafts]);

  // Wenn sich die Preis-Indizes ändern (z. B. Wissensdatenbank/Eigenpreise
  // werden im Hintergrund geladen), nur die preisbezogenen Felder an den
  // bestehenden Drafts nachziehen — nutzerbearbeitete Werte (menge, ep,
  // einheit, kurztext, checked) bleiben unverändert.
  useEffect(() => {
    setSuggestedDrafts((prev) => {
      if (prev.length === 0) return prev;
      let changed = false;
      const next = prev.map((d) => {
        const own = lookupOwnPrice(d.kurztext, d.einheit, d.menge || 0);
        if (
          own.median === d.ownMedian &&
          own.count === d.ownCount &&
          own.source === d.ownSource
        ) {
          return d;
        }
        changed = true;
        return {
          ...d,
          ownMedian: own.median,
          ownCount: own.count,
          ownSource: own.source,
        };
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownPriceIndex, libraryIndex]);

  function updateDraft(id: string, patch: Partial<SuggestedDraft>) {
    setSuggestedDrafts((prev) =>
      prev.map((d) => {
        if (d.id !== id) return d;
        const merged = { ...d, ...patch };
        // Wenn Menge, Name oder Einheit geändert → Wissensdatenbank neu
        // konsultieren (mengen-abhängig) und Badge/Quelle aktualisieren.
        if (
          Object.prototype.hasOwnProperty.call(patch, "menge") ||
          Object.prototype.hasOwnProperty.call(patch, "kurztext") ||
          Object.prototype.hasOwnProperty.call(patch, "einheit")
        ) {
          const own = lookupOwnPrice(merged.kurztext, merged.einheit, merged.menge || 0);
          merged.ownMedian = own.median;
          merged.ownCount = own.count;
          merged.ownSource = own.source;
          // Falls der Nutzer den EP noch nicht selbst überschrieben hat
          // (sprich: aktueller ep == alter Median, oder alter Median === null
          // und aktueller ep ≤ 0), EP aus neuem Lookup vorschlagen.
          const userHasOverridden =
            !Object.prototype.hasOwnProperty.call(patch, "ep") &&
            d.ownMedian !== null &&
            Math.abs(d.ep - d.ownMedian) > 0.01;
          if (!userHasOverridden && own.median !== null && !Object.prototype.hasOwnProperty.call(patch, "ep")) {
            merged.ep = Math.round(own.median * 100) / 100;
          }
        }
        return merged;
      }),
    );
  }

  const presetProjectName = presetProjectId ? selectedProject?.name ?? null : null;

  const existingCustomPositions = useMemo(() => {
    const ls = selectedProject?.lvState as Record<string, unknown> | null | undefined;
    const arr = ls && Array.isArray(ls.customPositionen) ? (ls.customPositionen as Array<Record<string, unknown>>) : [];
    return arr.map((p) => ({
      id: String(p.id),
      name: String(p.name ?? "(ohne Name)"),
      sektionId: String(p.sektionId ?? ""),
      hasBeschreibung: typeof p.beschreibung === "string" && (p.beschreibung as string).length > 0,
    }));
  }, [selectedProject]);

  function addFiles(files: File[]) {
    const errors: string[] = [];
    let totalBytes = images.reduce((s, i) => s + i.file.size, 0);
    const next: SelectedImage[] = [];
    for (const f of files) {
      if (images.length + next.length >= MAX_IMAGES) {
        errors.push(`Maximal ${MAX_IMAGES} Fotos pro Analyse.`);
        break;
      }
      if (!ACCEPTED_MIME.test(f.type)) {
        errors.push(`${f.name}: Format nicht unterstützt.`);
        continue;
      }
      if (totalBytes + f.size > MAX_TOTAL_BYTES) {
        errors.push(`${f.name}: Gesamtgröße über ${(MAX_TOTAL_BYTES / 1024 / 1024) | 0} MB.`);
        continue;
      }
      totalBytes += f.size;
      next.push({
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file: f,
        previewUrl: URL.createObjectURL(f),
      });
    }
    if (next.length > 0) setImages((prev) => [...prev, ...next]);
    if (errors.length > 0) {
      toast({ title: "Einige Dateien ignoriert", description: errors.join(" "), variant: "destructive" });
    }
  }

  function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (files.length > 0) addFiles(files);
    e.target.value = "";
  }

  function onDragOver(e: React.DragEvent) {
    e.preventDefault();
    setDragActive(true);
  }
  function onDragLeave(e: React.DragEvent) {
    e.preventDefault();
    setDragActive(false);
  }
  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragActive(false);
    const files = Array.from(e.dataTransfer.files ?? []).filter((f) => f.type.startsWith("image/"));
    if (files.length > 0) addFiles(files);
  }

  function removeImage(id: string) {
    setImages((prev) => {
      const target = prev.find((i) => i.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((i) => i.id !== id);
    });
  }

  const analyzeMutation = useMutation({
    mutationFn: async (payload: AnalyzePayload): Promise<PhotoAnalysis> => {
      const res = await apiRequest("POST", "/api/photo-analyses", payload);
      return (await res.json()) as PhotoAnalysis;
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ["/api/photo-analyses"] });
      setActiveAnalysis(created);
      setEditedText(created.generatedText);
      toast({ title: "Analyse fertig", description: "Die KI hat einen Vorschlag erzeugt." });
    },
    onError: (err: Error) => {
      toast({ title: "Analyse fehlgeschlagen", description: err.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, generatedText }: { id: number; generatedText: string }) => {
      const res = await apiRequest("PATCH", `/api/photo-analyses/${id}`, { generatedText });
      return (await res.json()) as PhotoAnalysis;
    },
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ["/api/photo-analyses"] });
      setActiveAnalysis(updated);
      toast({ title: "Gespeichert" });
    },
    onError: (err: Error) => toast({ title: "Fehler beim Speichern", description: err.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => apiRequest("DELETE", `/api/photo-analyses/${id}`),
    onSuccess: (_res, id) => {
      queryClient.invalidateQueries({ queryKey: ["/api/photo-analyses"] });
      if (activeAnalysis?.id === id) {
        setActiveAnalysis(null);
        setEditedText("");
      }
      setDeleteId(null);
      toast({ title: "Analyse gelöscht" });
    },
    onError: (err: Error) => toast({ title: "Fehler beim Löschen", description: err.message, variant: "destructive" }),
  });

  const adoptMutation = useMutation({
    mutationFn: async () => {
      if (!adoptProjectId) throw new Error("Bitte ein Projekt wählen.");
      if (adoptMode === "existing" && !adoptExistingId) {
        throw new Error("Bitte eine bestehende Position wählen.");
      }
      const projId = Number(adoptProjectId);
      const projRes = await apiRequest("GET", `/api/projects/${projId}`);
      const proj = (await projRes.json()) as Project & { lvState?: Record<string, unknown> | null };
      const lvState: Record<string, unknown> = {
        ...((proj.lvState as Record<string, unknown> | null) ?? {}),
      };
      const existing = Array.isArray(lvState.customPositionen)
        ? (lvState.customPositionen as Array<Record<string, unknown>>)
        : [];

      let writtenPositionId: string | null = null;
      let writtenName = "";
      let writtenSektionId = "";
      if (adoptMode === "existing") {
        const idx = existing.findIndex((p) => String(p.id) === adoptExistingId);
        if (idx < 0) throw new Error("Bestehende Position nicht mehr gefunden.");
        const target = { ...existing[idx] };
        const prev = typeof target.beschreibung === "string" ? (target.beschreibung as string) : "";
        target.beschreibung = adoptExistingMerge === "append" && prev
          ? `${prev}\n\n${editedText}`
          : editedText;
        const updated = [...existing];
        updated[idx] = target;
        lvState.customPositionen = updated;
        writtenPositionId = String(target.id ?? adoptExistingId);
        writtenName = typeof target.name === "string" ? (target.name as string) : "";
        writtenSektionId = typeof target.sektionId === "string" ? (target.sektionId as string) : "";
      } else {
        const newId = `custom-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const finalName = adoptName.trim() || `${REPORT_TYPE_LABEL[reportType]} (KI)`;
        // Negative Werte aus KI-Halluzinationen oder Tippfehlern abfangen.
        const safeEp = Number.isFinite(adoptEp) ? Math.max(0, adoptEp) : 0;
        const safeQty = Number.isFinite(adoptQty) ? Math.max(0, adoptQty) : 0;
        const newPosition = {
          id: newId,
          sektionId: adoptSektionId,
          name: finalName,
          unit: adoptUnit.trim() || "Psch",
          ep: safeEp,
          qty: safeQty,
          aktiv: true,
          tag: null,
          beschreibung: editedText,
        };
        lvState.customPositionen = [...existing, newPosition];
        writtenPositionId = newId;
        writtenName = finalName;
        writtenSektionId = adoptSektionId;
      }

      const patchRes = await apiRequest("PATCH", `/api/projects/${projId}/lv-state`, { lvState });
      const patched = (await patchRes.json()) as { ok: boolean; lvState?: unknown };
      return {
        projId,
        lvState: patched.lvState ?? lvState,
        positionId: writtenPositionId,
        name: writtenName,
        sektionId: writtenSektionId,
        projectName: proj.name ?? `Projekt #${projId}`,
        wasExisting: adoptMode === "existing",
      };
    },
    onSuccess: ({ projId, lvState, positionId, name, sektionId, projectName, wasExisting }) => {
      // Cache der Projekt-Detail-Query SYNCHRON mit dem frisch geschriebenen
      // lvState aktualisieren — sowohl unter dem String- als auch dem
      // Number-Key, damit jede aktuell gemountete Detailseite (project-edit
      // nutzt String) die neuen customPositionen sofort sieht und keine
      // Race mit einem stale Snapshot entsteht.
      const keyStr = String(projId);
      const cached = queryClient.getQueryData<Project & { lvState?: unknown }>(["/api/projects", keyStr]);
      if (cached) {
        queryClient.setQueryData(["/api/projects", keyStr], { ...cached, lvState });
      }
      // Prefix-Invalidierung trifft alle Varianten ("/api/projects",
      // "/api/projects/:id" mit String- und Number-Key) und stößt einen
      // Refetch an, sobald die Detailseite (re)mounted wird.
      queryClient.invalidateQueries({ queryKey: ["/api/projects"] });
      setAdoptOpen(false);

      const safeName = projectName?.trim() || `Projekt #${projId}`;
      setLastAdoptedProject({ id: projId, name: safeName });

      const sektion = STAMMDATEN.find((s) => s.id === sektionId);
      const sektionLabel = sektion ? `${sektion.icon} ${sektion.titel}` : sektionId;
      const namePart = name ? `„${name.length > 60 ? `${name.slice(0, 57)}…` : name}"` : "Position";
      const targetUrl = positionId
        ? `/project/edit/${projId}?focusLvPos=${encodeURIComponent(positionId)}`
        : `/project/edit/${projId}`;

      toast({
        title: wasExisting ? "Beschreibung aktualisiert" : "LV-Position ergänzt",
        description: wasExisting
          ? `${namePart} im Projekt „${safeName}" → ${sektionLabel}`
          : `${namePart} angelegt in „${safeName}" → ${sektionLabel}`,
        action: (
          <ToastAction
            altText="Im LV-Kalkulator anzeigen"
            onClick={() => setLocation(targetUrl)}
            data-testid="photo-analysis-adopt-toast-jump"
          >
            Im LV ansehen
          </ToastAction>
        ),
      });
    },
    onError: (err: Error) => toast({ title: "Übernahme fehlgeschlagen", description: err.message, variant: "destructive" }),
  });

  // Fotos des gewählten Projekts für die Ziel-Auswahl im Übernahme-Dialog.
  const { data: adoptMultiPhotos = [], isLoading: adoptMultiPhotosLoading } = useQuery<ServerPhotoMetadata[]>({
    queryKey: ["/api/projects", adoptMultiProjectId, "photos"],
    enabled: adoptMultiOpen && !!adoptMultiProjectId,
  });

  const adoptMultiTargetPhoto = useMemo(
    () => adoptMultiPhotos.find((p) => String(p.id) === adoptMultiPhotoId) ?? null,
    [adoptMultiPhotos, adoptMultiPhotoId],
  );

  // Übernahme MEHRERER Positionen auf einmal — schreibt die markierten Drafts
  // direkt an das gewählte Schadensbild (photoPositionen) und setzt, falls
  // das Foto noch keine Beschreibung hat, die aktuell bearbeitete
  // Leistungsbeschreibung.
  const adoptMultiMutation = useMutation({
    mutationFn: async () => {
      if (!adoptMultiProjectId) throw new Error("Bitte ein Projekt wählen.");
      if (!adoptMultiPhotoId) throw new Error("Bitte ein Schadensbild wählen.");
      const checked = suggestedDrafts.filter((d) => d.checked && d.kurztext.trim() && d.einheit.trim());
      if (checked.length === 0) throw new Error("Keine Positionen markiert.");
      const photoIdNum = Number(adoptMultiPhotoId);
      if (!Number.isInteger(photoIdNum) || photoIdNum <= 0) {
        throw new Error("Ungültige Foto-ID.");
      }
      const projId = Number(adoptMultiProjectId);

      // Aktuellen Stand des Fotos laden (Server ist Wahrheit).
      const photosRes = await apiRequest("GET", `/api/projects/${projId}/photos`);
      const allPhotos = (await photosRes.json()) as ServerPhotoMetadata[];
      const current = allPhotos.find((p) => p.id === photoIdNum);
      if (!current) throw new Error("Foto nicht mehr gefunden.");

      const existingPositionen: PhotoLVPosition[] = Array.isArray(current.photoPositionen)
        ? (current.photoPositionen as PhotoLVPosition[])
        : [];
      const existingIds = new Set(existingPositionen.map((p) => p.id));

      // Stabile IDs per Analyse + Draft-Index → erneute Übernahme derselben
      // Analyse dupliziert nichts. Vorhandene IDs werden übersprungen (der
      // Nutzer kann sie direkt am Foto bearbeiten, wenn er sie ändern will).
      const analysisId = activeAnalysis?.id ?? "x";
      const additions: PhotoLVPosition[] = [];
      let skippedDuplicates = 0;
      for (const d of checked) {
        const match = /^draft-[^-]+-(\d+)$/.exec(d.id);
        const idx = match ? match[1] : Math.random().toString(36).slice(2, 8);
        const stablePosId = `analysis-${analysisId}-${idx}`;
        if (existingIds.has(stablePosId)) {
          skippedDuplicates += 1;
          continue;
        }
        additions.push({
          id: stablePosId,
          name: d.kurztext.trim().slice(0, 300),
          unit: d.einheit.trim().slice(0, 20),
          ep: Number.isFinite(d.ep) && d.ep >= 0 ? d.ep : 0,
          qty: Number.isFinite(d.menge) && d.menge >= 0 ? d.menge : 0,
        });
      }

      if (additions.length === 0) {
        throw new Error(
          skippedDuplicates > 0
            ? "Alle markierten Positionen sind bereits an diesem Foto vorhanden."
            : "Keine neuen Positionen zu übernehmen.",
        );
      }

      const mergedPositionen = [...existingPositionen, ...additions];

      const patch: {
        description?: string;
        photoPositionen: PhotoLVPosition[];
      } = { photoPositionen: mergedPositionen };

      // Beschreibung nur setzen, wenn das Foto noch keine hat — vorhandene
      // Texte des Nutzers werden nie überschrieben.
      const existingDescription = typeof current.description === "string" ? current.description.trim() : "";
      const analysisText = editedText.trim();
      let descriptionSet = false;
      if (!existingDescription && analysisText) {
        patch.description = analysisText;
        descriptionSet = true;
      }

      await apiRequest("PATCH", `/api/photos/${photoIdNum}`, patch);

      // Projektnamen für den „Zum Projekt"-Sprung ermitteln. Bevorzugt aus
      // der bereits geladenen Projektliste (Dialog war offen → Query
      // enabled), sonst Fallback aus dem Detail-Cache, sonst „Projekt #ID".
      const fromList = projects.find((p) => p.id === projId)?.name;
      const fromCache = queryClient.getQueryData<Project>(["/api/projects", String(projId)])?.name;
      const projectName = fromList ?? fromCache ?? `Projekt #${projId}`;

      return {
        count: additions.length,
        skippedDuplicates,
        photoName: current.fileName,
        projId,
        photoIdNum,
        descriptionSet,
        projectName,
      };
    },
    onSuccess: ({ count, skippedDuplicates, photoName, projId, photoIdNum, descriptionSet, projectName }) => {
      // Caches aktualisieren: Fotos-Liste des Projekts und Projekt-Detail.
      queryClient.invalidateQueries({ queryKey: ["/api/projects", String(projId), "photos"] });
      queryClient.invalidateQueries({ queryKey: ["/api/projects"] });
      setAdoptMultiOpen(false);
      const parts = [`${count} Position${count === 1 ? "" : "en"} an „${photoName}" angehängt.`];
      if (descriptionSet) parts.push("Beschreibung des Fotos gesetzt.");
      if (skippedDuplicates > 0) parts.push(`${skippedDuplicates} Duplikat${skippedDuplicates === 1 ? "" : "e"} übersprungen.`);
      const safeName = projectName?.trim() || `Projekt #${projId}`;
      setLastAdoptedProject({ id: projId, name: safeName, photoId: photoIdNum });
      // Sprungziel: wenn wir die ID des Schadensbilds kennen, im Projekt-Editor
      // direkt zu diesem Foto in Schritt 3 scrollen + kurz hervorheben.
      const jumpUrl = Number.isInteger(photoIdNum) && photoIdNum > 0
        ? `/project/edit/${projId}?focusPhotoId=${photoIdNum}`
        : `/project/edit/${projId}`;
      toast({
        title: "Ans Schadensbild übernommen",
        description: parts.join(" "),
        action: (
          <ToastAction
            altText={`Zum Schadensbild „${photoName}" in „${safeName}" springen`}
            onClick={() => setLocation(jumpUrl)}
            data-testid="photo-analysis-toast-jump-multi"
          >
            Zum Foto
          </ToastAction>
        ),
      });
    },
    onError: (err: Error) =>
      toast({ title: "Übernahme fehlgeschlagen", description: err.message, variant: "destructive" }),
  });

  async function handleAnalyze() {
    if (images.length === 0) {
      toast({ title: "Keine Fotos", description: "Bitte mindestens ein Foto hochladen.", variant: "destructive" });
      return;
    }
    try {
      const payloadImages = await Promise.all(
        images.map(async (i) => {
          const compressed = await fileToCompressedBase64(i.file);
          return {
            fileName: i.file.name,
            mimeType: compressed.mimeType,
            data: compressed.data,
          };
        }),
      );
      analyzeMutation.mutate({ reportType, context: context.trim(), images: payloadImages });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Datei-Lesefehler";
      toast({ title: "Fehler beim Vorbereiten der Bilder", description: msg, variant: "destructive" });
    }
  }

  // Auto-Start: wurde die Seite per ?projectId=…&photoId=… geöffnet und das
  // Foto wurde erfolgreich vorgeladen, läuft die Analyse einmal automatisch
  // los, damit der Nutzer sofort die KI-Vorschläge sieht. Schutz gegen
  // Doppelstart über Ref + Mutations-Status.
  const autoAnalyzeStartedRef = useRef(false);
  useEffect(() => {
    if (!presetPhotoId) return;
    if (autoAnalyzeStartedRef.current) return;
    if (presetPhotoLoading || presetPhotoError) return;
    if (images.length === 0) return;
    if (analyzeMutation.isPending) return;
    if (activeAnalysis) return;
    autoAnalyzeStartedRef.current = true;
    void handleAnalyze();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    presetPhotoId,
    presetPhotoLoading,
    presetPhotoError,
    images.length,
    analyzeMutation.isPending,
    activeAnalysis,
  ]);

  async function handleCopy() {
    if (!editedText) return;
    try {
      await navigator.clipboard.writeText(editedText);
      toast({ title: "In Zwischenablage kopiert" });
    } catch {
      toast({ title: "Kopieren fehlgeschlagen", variant: "destructive" });
    }
  }

  function openAdoptDialog() {
    if (!editedText.trim()) {
      toast({ title: "Kein Text", description: "Bitte zuerst Text generieren oder eintippen.", variant: "destructive" });
      return;
    }
    // Bessere Defaults: zuerst aus den strukturierten KI-Vorschlägen
    // (kurztext / einheit / menge / ep) lesen, damit die neue LV-Position
    // nicht als nichtssagende „Leistung" mit qty 1 / EP 0 im Projekt landet.
    // Bevorzugt der erste angehakte Draft (das ist der „Hauptvorschlag",
    // den der Nutzer sehen wollte), sonst der erste Vorschlag überhaupt.
    const firstDraft =
      suggestedDrafts.find((d) => d.checked && d.kurztext.trim()) ??
      suggestedDrafts.find((d) => d.kurztext.trim()) ??
      null;

    let defaultName = "";
    if (firstDraft && firstDraft.kurztext.trim()) {
      defaultName = firstDraft.kurztext.trim();
    } else {
      // Heuristik für freien Text: erste Zeile, die mehr als nur ein
      // generisches Kapitel-Wort ist (sonst landet sie als „Leistung").
      const GENERIC_HEADERS = new Set([
        "leistung", "leistungen", "leistungsbeschreibung",
        "schadensbericht", "beschreibung", "übersicht", "ubersicht",
        "ausführungsbeschreibung", "ausfuehrungsbeschreibung",
        "zusammenfassung",
      ]);
      const lines = editedText
        .split("\n")
        .map((l) => l.replace(/^#+\s*/, "").replace(/^[-*]\s*/, "").trim())
        .filter((l) => l.length > 0);
      defaultName =
        lines.find((l) => l.length >= 6 && !GENERIC_HEADERS.has(l.toLowerCase())) ??
        lines[0] ??
        "";
    }
    if (!defaultName) {
      const today = new Date().toLocaleDateString("de-DE");
      defaultName = `${REPORT_TYPE_LABEL[reportType]} – ${today}`;
    }

    setAdoptName(defaultName.slice(0, 120));
    if (firstDraft) {
      if (firstDraft.einheit.trim()) setAdoptUnit(firstDraft.einheit.trim());
      if (firstDraft.ep > 0) setAdoptEp(Number(firstDraft.ep.toFixed(2)));
      if (firstDraft.menge > 0) setAdoptQty(Number(firstDraft.menge.toFixed(2)));
    }
    // Default-Sektion: „Leistungspositionen" (s8) statt s1, damit eine
    // neu angelegte Position in einer ansonsten meist leeren Gruppe
    // sichtbar ist und nicht zwischen vorhandenen Beton-Positionen verschwindet.
    if (adoptSektionId === "s1") {
      setAdoptSektionId("s8");
    }
    setAdoptOpen(true);
  }

  function loadFromHistory(item: PhotoAnalysis) {
    setActiveAnalysis(item);
    setEditedText(item.generatedText);
    setReportType(item.reportType as PhotoAnalysisReportType);
    setContext(item.context ?? "");
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }

  /**
   * Schnellweg aus der Historie: lädt einen historischen Eintrag als aktiven
   * Analyse-State (damit suggestedDrafts via useEffect neu aufgebaut werden)
   * UND öffnet sofort den Übernahme-Dialog für mehrere Positionen. So kann der
   * Nutzer aus einem ähnlichen, früheren Schadensbild die KI-Vorschläge direkt
   * an ein aktuelles Schadensbild im LV anhängen — ohne den Eintrag erst
   * aufrufen und Schritt für Schritt durchklicken zu müssen.
   */
  function quickAdoptFromHistory(item: PhotoAnalysis) {
    if (!Array.isArray(item.suggestedPositions) || item.suggestedPositions.length === 0) {
      toast({
        title: "Keine Positionen vorhanden",
        description: "Dieser Eintrag enthält keine KI-Positionsvorschläge zum Übernehmen.",
        variant: "destructive",
      });
      return;
    }
    setActiveAnalysis(item);
    setEditedText(item.generatedText);
    setReportType(item.reportType as PhotoAnalysisReportType);
    setContext(item.context ?? "");
    if (presetProjectId) setAdoptMultiProjectId(presetProjectId);
    // Den Dialog NICHT direkt öffnen — sonst räumt der Drafts-useEffect die
    // Drafts noch im selben Tick mit den neuen Daten neu auf, der Dialog
    // würde aber ein Render lang die alten Drafts (oder leer) zeigen.
    // Stattdessen: pending-Flag setzen → der zweite useEffect öffnet den
    // Dialog erst, wenn die Drafts der Zielanalyse bereit sind.
    setPendingAdoptForAnalysisId(item.id);
  }

  const totalSizeMb = useMemo(
    () => (images.reduce((s, i) => s + i.file.size, 0) / 1024 / 1024).toFixed(1),
    [images],
  );

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
              <Sparkles className="w-7 h-7 text-bauwerk-teal" />
              Foto-Analyse
            </h1>
            <p className="text-gray-600">
              Lade Fotos hoch und lass die KI daraus einen Schadensbericht oder eine Leistungsbeschreibung
              entwerfen — den Text kannst du anschließend bearbeiten und direkt in eine LV-Position übernehmen.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => setLocation("/dashboard")}
            className="border-gray-300 text-gray-700 hover:bg-gray-50 flex-shrink-0"
            data-testid="button-back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zurück zum Dashboard
          </Button>
        </div>

        {presetProjectId && (
          <div
            className="rounded-md border border-bauwerk-mint bg-bauwerk-mint/30 px-4 py-3 flex items-center justify-between gap-3"
            data-testid="photo-analysis-project-context"
          >
            <div className="text-sm text-gray-800">
              <span className="font-medium">Projektkontext:</span>{" "}
              {presetProjectName ?? `Projekt #${presetProjectId}`}
              <span className="block text-xs text-gray-600">
                Übernommene Texte landen ohne weiteren Projekt-Picker direkt im LV dieses Projekts.
              </span>
            </div>
            <Link href={`/project/edit/${presetProjectId}`}>
              <Button variant="outline" size="sm" data-testid="photo-analysis-back-to-project">
                <ArrowLeft className="w-4 h-4 mr-2" /> Zurück zum Projekt
              </Button>
            </Link>
          </div>
        )}

        {lastAdoptedProject && (
          <div
            className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-center justify-between gap-3"
            data-testid="photo-analysis-jump-banner"
          >
            <div className="text-sm text-gray-800">
              <span className="font-medium">Zuletzt befüllt:</span> {lastAdoptedProject.name}
              <span className="block text-xs text-gray-600">
                Direkt ins Projekt springen, ohne Umweg über das Dashboard.
              </span>
            </div>
            <Button
              size="sm"
              onClick={() => {
                const pid = lastAdoptedProject.photoId;
                const url = Number.isInteger(pid) && pid! > 0
                  ? `/project/edit/${lastAdoptedProject.id}?focusPhotoId=${pid}`
                  : `/project/edit/${lastAdoptedProject.id}`;
                setLocation(url);
              }}
              className="bg-bauwerk-teal hover:bg-bauwerk-teal/90 text-white"
              data-testid="photo-analysis-jump-to-project"
            >
              {lastAdoptedProject.photoId
                ? <>Zum Foto springen <ArrowRight className="w-4 h-4 ml-2" /></>
                : <>Zum Projekt springen <ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Camera className="w-5 h-5 text-bauwerk-teal" /> Fotos &amp; Eingaben
              </CardTitle>
              <CardDescription>
                Maximal {MAX_IMAGES} Bilder, gesamt bis ca. {(MAX_TOTAL_BYTES / 1024 / 1024) | 0} MB. Formate: JPG, PNG, WEBP, GIF.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {presetPhotoId && (presetPhotoLoading || !presetPhotoError) ? (
                <div
                  className="rounded-md border border-bauwerk-teal/40 bg-bauwerk-teal/5 px-3 py-3 flex items-start gap-3"
                  data-testid="photo-analysis-preset-photo-banner"
                >
                  {presetPhotoLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mt-0.5 animate-spin text-bauwerk-teal shrink-0" />
                      <div className="text-sm text-gray-700">
                        Foto aus dem Projekt wird geladen …
                      </div>
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mt-0.5 text-bauwerk-teal shrink-0" />
                      <div className="text-sm text-gray-800">
                        <div className="font-medium">Foto aus dem Projekt vorgeladen</div>
                        <div className="text-xs text-gray-600">
                          Die Analyse läuft automatisch und übernimmt am Ende ans gewählte
                          Schadensbild — kein erneuter Upload nötig.
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <>
                  {presetPhotoId && presetPhotoError && (
                    <div
                      className="rounded-md border border-red-200 bg-red-50 px-3 py-3 flex items-start gap-3"
                      data-testid="photo-analysis-preset-photo-error"
                    >
                      <Info className="w-5 h-5 mt-0.5 text-red-600 shrink-0" />
                      <div className="flex-1 text-sm text-red-700">
                        <div className="font-medium">Foto konnte nicht vorgeladen werden</div>
                        <div className="text-xs">
                          {presetPhotoError} · Sie können das Foto manuell hochladen oder es erneut versuchen.
                        </div>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => window.location.reload()}
                        data-testid="photo-analysis-preset-photo-retry"
                      >
                        Erneut laden
                      </Button>
                    </div>
                  )}
                  <div
                    ref={dropRef}
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                    onDrop={onDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-lg px-4 py-8 text-center cursor-pointer transition-colors ${
                      dragActive
                        ? "border-bauwerk-teal bg-bauwerk-teal/5"
                        : "border-gray-300 hover:border-bauwerk-teal/60 hover:bg-gray-50"
                    }`}
                    data-testid="photo-analysis-dropzone"
                  >
                    <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                    <p className="text-sm text-gray-700">
                      Fotos hier ablegen oder <span className="text-bauwerk-teal font-medium">auswählen</span>
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {images.length > 0 ? `${images.length} Foto(s) · ${totalSizeMb} MB` : "Noch keine Fotos ausgewählt"}
                    </p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/png,image/jpeg,image/webp,image/gif"
                      className="hidden"
                      onChange={onFileChange}
                      data-testid="photo-analysis-file-input"
                    />
                  </div>
                </>
              )}

              {images.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                  {images.map((img) => {
                    const isPreset = presetPhotoId !== "" && img.id === `preset-photo-${presetPhotoId}`;
                    return (
                      <div key={img.id} className="relative group rounded-md overflow-hidden border border-gray-200 bg-gray-50">
                        <img src={img.previewUrl} alt={img.file.name} className="w-full h-24 object-cover" />
                        {!isPreset && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); removeImage(img.id); }}
                            className="absolute top-1 right-1 bg-white/90 hover:bg-white text-red-600 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            aria-label="Entfernen"
                            data-testid={`photo-analysis-remove-${img.id}`}
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <div className="text-[10px] truncate px-1 py-0.5 bg-white/80">{img.file.name}</div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div>
                <Label className="mb-2 block">Berichtstyp</Label>
                <RadioGroup
                  value={reportType}
                  onValueChange={(v) => setReportType(v as PhotoAnalysisReportType)}
                  className="grid grid-cols-1 sm:grid-cols-2 gap-2"
                >
                  {(Object.keys(REPORT_TYPE_LABEL) as PhotoAnalysisReportType[]).map((rt) => (
                    <label
                      key={rt}
                      htmlFor={`rt-${rt}`}
                      className={`flex items-center gap-2 rounded-md border px-3 py-2 cursor-pointer ${
                        reportType === rt ? "border-bauwerk-teal bg-bauwerk-teal/5" : "border-gray-200"
                      }`}
                    >
                      <RadioGroupItem id={`rt-${rt}`} value={rt} />
                      <span className="text-sm">{REPORT_TYPE_LABEL[rt]}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>

              <div>
                <Label htmlFor="ctx" className="mb-1 block">Kontext (optional)</Label>
                <Textarea
                  id="ctx"
                  value={context}
                  onChange={(e) => setContext(e.target.value)}
                  placeholder='z. B. "Stützenfuß Tiefgarage, Bj. 1978, Karbonatisierung vermutet"'
                  rows={3}
                  data-testid="photo-analysis-context"
                />
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={handleAnalyze}
                  disabled={analyzeMutation.isPending || images.length === 0}
                  className="bg-bauwerk-teal hover:bg-bauwerk-teal/90 text-white"
                  data-testid="photo-analysis-analyze"
                >
                  {analyzeMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> KI analysiert …
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" /> Analysieren
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <ClipboardList className="w-5 h-5 text-bauwerk-teal" /> Verlauf
              </CardTitle>
              <CardDescription>Letzte Analysen — anklicken zum erneuten Bearbeiten.</CardDescription>
            </CardHeader>
            <CardContent>
              {historyLoading ? (
                <div className="text-center py-6 text-muted-foreground">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                </div>
              ) : history.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">Noch keine Analysen.</p>
              ) : (
                <ul className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                  {history.map((h) => {
                    const imgs = Array.isArray(h.images) ? h.images : [];
                    const positionsCount = Array.isArray(h.suggestedPositions) ? h.suggestedPositions.length : 0;
                    return (
                      <li
                        key={h.id}
                        className={`group rounded-md border px-3 py-2 cursor-pointer transition-colors ${
                          activeAnalysis?.id === h.id ? "border-bauwerk-teal bg-bauwerk-teal/5" : "border-gray-200 hover:bg-gray-50"
                        }`}
                        onClick={() => loadFromHistory(h)}
                        data-testid={`photo-analysis-history-${h.id}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                              <Badge variant="outline" className="text-[10px]">
                                {REPORT_TYPE_LABEL[h.reportType as PhotoAnalysisReportType] ?? h.reportType}
                              </Badge>
                              <span className="text-[11px] text-gray-500">
                                {h.createdAt ? new Date(h.createdAt).toLocaleString("de-DE") : ""}
                              </span>
                              {positionsCount > 0 && (
                                <Badge variant="secondary" className="text-[10px]">
                                  {positionsCount} {positionsCount === 1 ? "Position" : "Positionen"}
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-gray-700 line-clamp-2">
                              {h.generatedText.replace(/[#*_`>-]/g, " ").slice(0, 140) || "—"}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setDeleteId(h.id); }}
                            className="text-red-500 opacity-0 group-hover:opacity-100 transition-opacity p-1 shrink-0"
                            aria-label="Löschen"
                            data-testid={`photo-analysis-delete-${h.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        {/* Thumbnails der zur Analyse gehörenden Bilder. Werden über
                            den dedizierten Image-Endpunkt nachgeladen, da der
                            Listen-Endpunkt die Bilddaten strippt. */}
                        {imgs.length > 0 && (
                          <div className="mt-2 flex gap-1.5 flex-wrap" data-testid={`photo-analysis-history-images-${h.id}`}>
                            {imgs.map((img, idx) => (
                              <img
                                key={`${h.id}-${idx}`}
                                src={`/api/photo-analyses/${h.id}/images/${idx}`}
                                alt={img.fileName || `Bild ${idx + 1}`}
                                title={img.fileName}
                                className="w-12 h-12 object-cover rounded border border-gray-200 bg-gray-50"
                                loading="lazy"
                              />
                            ))}
                          </div>
                        )}

                        {positionsCount > 0 && (
                          <div className="mt-2">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-7 text-[11px] px-2 border-bauwerk-teal/40 text-bauwerk-teal hover:bg-bauwerk-teal/10"
                              onClick={(e) => { e.stopPropagation(); quickAdoptFromHistory(h); }}
                              data-testid={`photo-analysis-history-adopt-${h.id}`}
                            >
                              <FilePlus2 className="w-3 h-3 mr-1" /> Positionen ans Schadensbild übernehmen
                            </Button>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <FilePlus2 className="w-5 h-5 text-bauwerk-teal" /> Ergebnis
            </CardTitle>
            <CardDescription>
              Bearbeite den Text frei. „Speichern" sichert die Änderungen im Verlauf.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Textarea
              value={editedText}
              onChange={(e) => setEditedText(e.target.value)}
              placeholder="Hier erscheint der von der KI erzeugte Vorschlag — du kannst ihn frei überarbeiten."
              rows={16}
              className="font-mono text-sm"
              data-testid="photo-analysis-result"
            />
            <div
              className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-start gap-2"
              data-testid="photo-analysis-adopt-help"
            >
              <Info className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <div className="font-medium">Bericht ins Projekt-LV ergänzen</div>
                Legt aus dem oben bearbeiteten Text EINE neue Position im Leistungsverzeichnis des Projekts an
                (Schritt 4 „LV-Kalkulator"). Der ganze Berichtstext wird zur Beschreibung dieser Position.
                Für mehrere Positionen aus den KI-Vorschlägen unten den Knopf „Markierte an Schadensbild anhängen" nutzen.
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-xs text-gray-500">
                {activeAnalysis
                  ? `Aktive Analyse #${activeAnalysis.id} · ${new Date(activeAnalysis.createdAt ?? new Date()).toLocaleString("de-DE")}`
                  : "Noch keine Analyse aktiv."}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  onClick={handleCopy}
                  disabled={!editedText}
                  data-testid="photo-analysis-copy"
                >
                  <Copy className="w-4 h-4 mr-2" /> In Zwischenablage kopieren
                </Button>
                <Button
                  variant="outline"
                  onClick={() => activeAnalysis && updateMutation.mutate({ id: activeAnalysis.id, generatedText: editedText })}
                  disabled={!activeAnalysis || updateMutation.isPending || editedText === activeAnalysis?.generatedText}
                  data-testid="photo-analysis-save"
                >
                  {updateMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Speichern
                </Button>
                <Button
                  onClick={openAdoptDialog}
                  disabled={!editedText.trim()}
                  className="bg-bauwerk-teal hover:bg-bauwerk-teal/90 text-white"
                  data-testid="photo-analysis-adopt"
                  title="Legt EINE neue LV-Position im Projekt-LV an. Der gesamte Berichtstext wird als Beschreibung dieser Position eingefügt."
                >
                  <FileText className="w-4 h-4 mr-2" /> Bericht ins Projekt-LV ergänzen
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {suggestedDrafts.length > 0 && (
          <Card data-testid="photo-analysis-suggestions">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <FilePlus2 className="w-5 h-5 text-bauwerk-teal" /> LV-Positionsvorschläge
              </CardTitle>
              <CardDescription>
                Aus den Fotos abgeleitete Positionen mit marktüblicher Preisspanne (AT/DE, Stand 2026).
                Wo du schon eigene Positionen mit gleichem Kurztext &amp; Einheit hast, wird der
                Einzelpreis mit deinem eigenen Median vorbelegt.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between text-xs text-gray-600">
                <span>
                  {suggestedDrafts.filter((d) => d.checked).length} von {suggestedDrafts.length} markiert
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="underline hover:text-gray-800"
                    onClick={() => setSuggestedDrafts((prev) => prev.map((d) => ({ ...d, checked: true })))}
                  >
                    Alle
                  </button>
                  <button
                    type="button"
                    className="underline hover:text-gray-800"
                    onClick={() => setSuggestedDrafts((prev) => prev.map((d) => ({ ...d, checked: false })))}
                  >
                    Keine
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                {suggestedDrafts.map((d) => {
                  const sum = (d.ep || 0) * (d.menge || 0);
                  return (
                    <div
                      key={d.id}
                      className={`rounded-md border p-3 ${
                        d.checked ? "border-bauwerk-teal/50 bg-bauwerk-teal/5" : "border-gray-200 bg-white"
                      }`}
                      data-testid={`photo-analysis-suggestion-${d.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={d.checked}
                          onCheckedChange={(v) => updateDraft(d.id, { checked: v === true })}
                          className="mt-1"
                          data-testid={`photo-analysis-suggestion-check-${d.id}`}
                        />
                        <div className="flex-1 min-w-0 space-y-2">
                          <Input
                            value={d.kurztext}
                            onChange={(e) => updateDraft(d.id, { kurztext: e.target.value })}
                            className="font-medium"
                          />
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            <div>
                              <Label className="text-[11px] text-gray-500">Einheit</Label>
                              <Input
                                value={d.einheit}
                                onChange={(e) => updateDraft(d.id, { einheit: e.target.value })}
                                className="h-8"
                              />
                            </div>
                            <div>
                              <Label className="text-[11px] text-gray-500">Menge</Label>
                              <Input
                                type="number"
                                step="0.01"
                                value={d.menge}
                                onChange={(e) => updateDraft(d.id, { menge: Number(e.target.value) || 0 })}
                                className="h-8"
                              />
                            </div>
                            <div>
                              <Label className="text-[11px] text-gray-500">EP €</Label>
                              <Input
                                type="number"
                                step="0.01"
                                value={d.ep}
                                onChange={(e) => updateDraft(d.id, { ep: Number(e.target.value) || 0 })}
                                className="h-8"
                              />
                            </div>
                            <div>
                              <Label className="text-[11px] text-gray-500">Summe</Label>
                              <div className="h-8 flex items-center px-2 text-sm font-medium">{fmtEur(sum)}</div>
                            </div>
                          </div>

                          <div className="flex flex-wrap items-center gap-2 text-[11px]">
                            {d.einzelpreisVon > 0 || d.einzelpreisBis > 0 ? (
                              <Badge variant="outline" className="text-[10px]">
                                KI-Marktpreis: {fmtEur(d.einzelpreisVon)}–{fmtEur(d.einzelpreisBis)}/{d.einheit}
                              </Badge>
                            ) : null}
                            {d.ownMedian !== null && d.ownSource === "library" && (
                              <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-[10px]">
                                Wissensdatenbank (Menge {d.menge} {d.einheit}): {fmtEur(d.ownMedian)}/{d.einheit} · {d.ownCount} Sample{d.ownCount === 1 ? "" : "s"}
                              </Badge>
                            )}
                            {d.ownMedian !== null && d.ownSource === "projekt" && (
                              <Badge className="bg-sky-100 text-sky-800 hover:bg-sky-100 text-[10px]">
                                Eigene Projekt-Historie: {fmtEur(d.ownMedian)}/{d.einheit} · {d.ownCount}×
                              </Badge>
                            )}
                          </div>

                          {d.begruendung && (
                            <p className="text-[11px] text-gray-500 italic">{d.begruendung}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div
                className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900 flex items-start gap-2"
                data-testid="photo-analysis-adopt-multi-help"
              >
                <Info className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  <div className="font-medium">Markierte an Schadensbild anhängen</div>
                  Hängt die angehakten Vorschläge als <strong>mehrere</strong> Einzel-Positionen an
                  EIN bestehendes Schadensfoto des Projekts an (Schritt 3 „Bestandsaufnahme").
                  Sie erscheinen in der Schadens-Übersicht des Fotos, NICHT als eigene Positionen
                  im Projekt-LV. Falls noch keine Foto-Beschreibung existiert, wird der oben bearbeitete
                  Berichtstext zusätzlich als Foto-Beschreibung gespeichert.
                </div>
              </div>

              <div className="flex items-center justify-between pt-1">
                <div className="text-sm text-gray-700">
                  Zwischensumme markiert:{" "}
                  <span className="font-semibold">
                    {fmtEur(
                      suggestedDrafts
                        .filter((d) => d.checked)
                        .reduce((s, d) => s + (d.ep || 0) * (d.menge || 0), 0),
                    )}
                  </span>
                </div>
                <Button
                  onClick={() => {
                    if (presetProjectId) setAdoptMultiProjectId(presetProjectId);
                    setAdoptMultiOpen(true);
                  }}
                  disabled={suggestedDrafts.filter((d) => d.checked).length === 0}
                  className="bg-bauwerk-teal hover:bg-bauwerk-teal/90 text-white"
                  data-testid="photo-analysis-adopt-multi"
                  title="Hängt die markierten Vorschläge als einzelne Positionen an EIN bestehendes Schadensbild des Projekts an."
                >
                  <ImageIcon className="w-4 h-4 mr-2" /> Markierte an Schadensbild anhängen
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <Dialog open={adoptOpen} onOpenChange={setAdoptOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Bericht ins Projekt-LV ergänzen</DialogTitle>
            <DialogDescription>
              Legt EINE neue LV-Position im gewählten Projekt an (oder ergänzt eine bestehende).
              Der oben bearbeitete Berichtstext wird als Beschreibung der Position eingefügt.
              Bezeichnung, Einheit, Menge und Einzelpreis sind aus dem ersten KI-Vorschlag vorbelegt — bitte vor dem Speichern prüfen.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Projekt</Label>
              {presetProjectId ? (
                <div
                  className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm"
                  data-testid="photo-analysis-adopt-project-locked"
                >
                  <span className="truncate">
                    {presetProjectName ?? `Projekt #${presetProjectId}`}
                  </span>
                  <Badge variant="outline" className="text-[10px]">vorausgewählt</Badge>
                </div>
              ) : (
                <Select value={adoptProjectId} onValueChange={(v) => { setAdoptProjectId(v); setAdoptExistingId(""); }}>
                  <SelectTrigger data-testid="photo-analysis-adopt-project"><SelectValue placeholder="Projekt wählen …" /></SelectTrigger>
                  <SelectContent>
                    {projects.length === 0 ? (
                      <div className="px-2 py-1.5 text-sm text-muted-foreground">Keine Projekte vorhanden.</div>
                    ) : (
                      projects.map((p) => (
                        <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div>
              <Label className="mb-2 block">Ziel-Position</Label>
              <RadioGroup
                value={adoptMode}
                onValueChange={(v) => setAdoptMode(v as "new" | "existing")}
                className="grid grid-cols-2 gap-2"
              >
                <label
                  htmlFor="adopt-mode-new"
                  className={`flex items-center gap-2 rounded-md border px-3 py-2 cursor-pointer ${
                    adoptMode === "new" ? "border-bauwerk-teal bg-bauwerk-teal/5" : "border-gray-200"
                  }`}
                >
                  <RadioGroupItem id="adopt-mode-new" value="new" data-testid="photo-analysis-adopt-mode-new" />
                  <span className="text-sm">Neue Position</span>
                </label>
                <label
                  htmlFor="adopt-mode-existing"
                  className={`flex items-center gap-2 rounded-md border px-3 py-2 cursor-pointer ${
                    adoptMode === "existing" ? "border-bauwerk-teal bg-bauwerk-teal/5" : "border-gray-200"
                  } ${!adoptProjectId || existingCustomPositions.length === 0 ? "opacity-60" : ""}`}
                >
                  <RadioGroupItem
                    id="adopt-mode-existing"
                    value="existing"
                    disabled={!adoptProjectId || existingCustomPositions.length === 0}
                    data-testid="photo-analysis-adopt-mode-existing"
                  />
                  <span className="text-sm">
                    Bestehende Position
                    {adoptProjectId && existingCustomPositions.length === 0 && (
                      <span className="block text-[11px] text-gray-500">Keine vorhanden</span>
                    )}
                  </span>
                </label>
              </RadioGroup>
            </div>

            {adoptMode === "existing" ? (
              <>
                <div>
                  <Label>Bestehende Position</Label>
                  <Select value={adoptExistingId} onValueChange={setAdoptExistingId}>
                    <SelectTrigger data-testid="photo-analysis-adopt-existing">
                      <SelectValue placeholder="Position wählen …" />
                    </SelectTrigger>
                    <SelectContent>
                      {existingCustomPositions.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name}{p.hasBeschreibung ? "  •  hat bereits Beschreibung" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="mb-2 block">Vorhandene Beschreibung</Label>
                  <RadioGroup
                    value={adoptExistingMerge}
                    onValueChange={(v) => setAdoptExistingMerge(v as "replace" | "append")}
                    className="grid grid-cols-2 gap-2"
                  >
                    <label htmlFor="merge-replace" className={`flex items-center gap-2 rounded-md border px-3 py-2 cursor-pointer ${adoptExistingMerge === "replace" ? "border-bauwerk-teal bg-bauwerk-teal/5" : "border-gray-200"}`}>
                      <RadioGroupItem id="merge-replace" value="replace" />
                      <span className="text-sm">Ersetzen</span>
                    </label>
                    <label htmlFor="merge-append" className={`flex items-center gap-2 rounded-md border px-3 py-2 cursor-pointer ${adoptExistingMerge === "append" ? "border-bauwerk-teal bg-bauwerk-teal/5" : "border-gray-200"}`}>
                      <RadioGroupItem id="merge-append" value="append" />
                      <span className="text-sm">Anhängen</span>
                    </label>
                  </RadioGroup>
                </div>
              </>
            ) : (
              <>
                <div>
                  <Label>Leistungsgruppe</Label>
                  <Select value={adoptSektionId} onValueChange={setAdoptSektionId}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STAMMDATEN.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.icon} {s.titel}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Bezeichnung</Label>
                  <Input
                    value={adoptName}
                    onChange={(e) => setAdoptName(e.target.value)}
                    placeholder="Kurze Positions-Bezeichnung"
                    data-testid="photo-analysis-adopt-name"
                  />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label>Einheit</Label>
                    <Input value={adoptUnit} onChange={(e) => setAdoptUnit(e.target.value)} />
                  </div>
                  <div>
                    <Label>EP €</Label>
                    <Input type="number" step="0.01" value={adoptEp} onChange={(e) => setAdoptEp(Number(e.target.value) || 0)} />
                  </div>
                  <div>
                    <Label>Menge</Label>
                    <Input type="number" step="0.01" value={adoptQty} onChange={(e) => setAdoptQty(Number(e.target.value) || 0)} />
                  </div>
                </div>
              </>
            )}

            <div className="rounded-md bg-gray-50 border border-gray-200 p-2 max-h-32 overflow-y-auto text-xs whitespace-pre-wrap">
              {editedText}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdoptOpen(false)}>Abbrechen</Button>
            <Button
              onClick={() => adoptMutation.mutate()}
              disabled={adoptMutation.isPending || !adoptProjectId}
              className="bg-bauwerk-teal hover:bg-bauwerk-teal/90 text-white"
              data-testid="photo-analysis-adopt-confirm"
            >
              {adoptMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Übernehmen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={adoptMultiOpen} onOpenChange={setAdoptMultiOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Markierte Positionen ans Schadensbild übernehmen</DialogTitle>
            <DialogDescription>
              Es werden {suggestedDrafts.filter((d) => d.checked).length} Position(en) an das
              gewählte Schadensbild angehängt. Hat das Foto noch keine Beschreibung, wird der
              aktuelle Analyse-Text übernommen.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Projekt</Label>
              {presetProjectId ? (
                <div className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
                  <span className="truncate">{presetProjectName ?? `Projekt #${presetProjectId}`}</span>
                  <Badge variant="outline" className="text-[10px]">vorausgewählt</Badge>
                </div>
              ) : (
                <Select
                  value={adoptMultiProjectId}
                  onValueChange={(v) => { setAdoptMultiProjectId(v); setAdoptMultiPhotoId(""); }}
                >
                  <SelectTrigger data-testid="photo-analysis-adopt-multi-project">
                    <SelectValue placeholder="Projekt wählen …" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.length === 0 ? (
                      <div className="px-2 py-1.5 text-sm text-muted-foreground">Keine Projekte vorhanden.</div>
                    ) : (
                      projects.map((p) => (
                        <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div>
              <Label>Schadensbild</Label>
              {presetPhotoId ? (
                adoptMultiPhotosLoading && !adoptMultiTargetPhoto ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Foto wird geladen …
                  </div>
                ) : adoptMultiTargetPhoto ? (
                  <div
                    className="mt-1 flex items-start gap-3 rounded-md border border-gray-200 bg-gray-50 p-2"
                    data-testid="photo-analysis-adopt-multi-photo-locked"
                  >
                    <img
                      src={`/api/photos/${adoptMultiTargetPhoto.id}`}
                      alt={adoptMultiTargetPhoto.fileName}
                      loading="lazy"
                      className="w-14 h-14 object-cover rounded border border-gray-200 bg-gray-100 shrink-0"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium truncate">{adoptMultiTargetPhoto.fileName}</span>
                        <Badge variant="outline" className="text-[10px]">vorausgewählt</Badge>
                      </div>
                      <p className="text-[11px] text-gray-600 mt-0.5">
                        Übernahme erfolgt an dieses Foto aus dem Projekt-Editor.
                      </p>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground italic mt-1">
                    Foto #{presetPhotoId} im Projekt nicht gefunden.
                  </p>
                )
              ) : !adoptMultiProjectId ? (
                <p className="text-xs text-muted-foreground italic mt-1">Erst Projekt wählen.</p>
              ) : adoptMultiPhotosLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Fotos werden geladen …
                </div>
              ) : adoptMultiPhotos.length === 0 ? (
                <p className="text-xs text-muted-foreground italic mt-1">
                  Keine Fotos in diesem Projekt. Bitte zuerst Schadensbilder hochladen.
                </p>
              ) : (
                <div
                  className="mt-1 max-h-72 overflow-y-auto rounded-md border border-gray-200 divide-y divide-gray-100"
                  role="radiogroup"
                  aria-label="Schadensbild wählen"
                  data-testid="photo-analysis-adopt-multi-photo"
                >
                  {adoptMultiPhotos.map((p) => {
                    const desc = typeof p.description === "string" ? p.description.trim() : "";
                    const hasDesc = desc.length > 0;
                    const posCount = Array.isArray(p.photoPositionen) ? p.photoPositionen.length : 0;
                    const selected = adoptMultiPhotoId === String(p.id);
                    return (
                      <label
                        key={p.id}
                        htmlFor={`adopt-multi-photo-${p.id}`}
                        className={`flex items-start gap-3 p-2 cursor-pointer transition-colors ${
                          selected ? "bg-bauwerk-teal/10" : "hover:bg-gray-50"
                        }`}
                        data-testid={`photo-analysis-adopt-multi-photo-option-${p.id}`}
                      >
                        <input
                          type="radio"
                          id={`adopt-multi-photo-${p.id}`}
                          name="adopt-multi-photo"
                          className="mt-1 accent-bauwerk-teal"
                          checked={selected}
                          onChange={() => setAdoptMultiPhotoId(String(p.id))}
                        />
                        <img
                          src={`/api/photos/${p.id}`}
                          alt={p.fileName}
                          loading="lazy"
                          className="w-14 h-14 object-cover rounded border border-gray-200 bg-gray-100 shrink-0"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium truncate">{p.fileName}</span>
                            {posCount > 0 && (
                              <Badge variant="outline" className="text-[10px]">{posCount} Pos.</Badge>
                            )}
                            {hasDesc ? (
                              <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
                                Beschreibung vorhanden
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">
                                Keine Beschreibung
                              </Badge>
                            )}
                          </div>
                          {hasDesc ? (
                            <p className="text-[11px] text-gray-600 mt-0.5 line-clamp-2 whitespace-pre-wrap">
                              {desc}
                            </p>
                          ) : (
                            <p className="text-[11px] text-gray-400 italic mt-0.5">
                              Noch keine Beschreibung am Foto.
                            </p>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
              {adoptMultiTargetPhoto && (
                <p className="text-[11px] text-muted-foreground mt-1">
                  {typeof adoptMultiTargetPhoto.description === "string" && adoptMultiTargetPhoto.description.trim().length > 0
                    ? "Beschreibung vorhanden — wird nicht überschrieben."
                    : "Beschreibung wird mit dem aktuellen Analyse-Text gesetzt."}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdoptMultiOpen(false)}>Abbrechen</Button>
            <Button
              onClick={() => adoptMultiMutation.mutate()}
              disabled={adoptMultiMutation.isPending || !adoptMultiProjectId || !adoptMultiPhotoId}
              className="bg-bauwerk-teal hover:bg-bauwerk-teal/90 text-white"
              data-testid="photo-analysis-adopt-multi-confirm"
            >
              {adoptMultiMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Übernehmen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteId !== null} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Analyse löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Diese Analyse wird unwiderruflich aus dem Verlauf entfernt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteId !== null && deleteMutation.mutate(deleteId)}
              className="bg-red-600 hover:bg-red-700"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
